import { useRef, useState } from 'react'
import textServices from 'services/text-services'
import VideoLightbox from 'components/video-lightbox'
import { dlTrackEvent } from 'components/dataLayer/dataLayer'
import { AFTERWORK_ASSETS, INSIDE_EDITIONS, AfterworkInsideEdition } from '../constants'

// Dot-driven carousel (matches the Figma dots): tapping a dot scrolls the
// snap track to that edition; the dots stay in sync while scrolling. Tapping
// a card opens the shared video lightbox.
const InsideSection = () => {
  const [activeEdition, setActiveEdition] = useState<AfterworkInsideEdition | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const carouselRef = useRef<HTMLDivElement>(null)

  const cardStep = () => {
    const track = carouselRef.current
    if (!track || track.children.length < 2) return 0
    const first = track.children[0] as HTMLElement
    const second = track.children[1] as HTMLElement
    return second.offsetLeft - first.offsetLeft
  }

  const scrollToIndex = (index: number) => {
    const track = carouselRef.current
    if (!track) return
    const clamped = Math.max(0, Math.min(INSIDE_EDITIONS.length - 1, index))
    track.scrollTo({ left: clamped * cardStep(), behavior: 'smooth' })
  }

  const handleScroll = () => {
    const track = carouselRef.current
    const step = cardStep()
    if (!track || !step) return
    // Wide viewports saturate the scroll before the last card index: when the
    // track hits its physical end, the last dot is the active one.
    const atPhysicalEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4
    setActiveIndex(
      atPhysicalEnd
        ? INSIDE_EDITIONS.length - 1
        : Math.max(0, Math.min(INSIDE_EDITIONS.length - 1, Math.round(track.scrollLeft / step)))
    )
  }

  return (
    <section className="afterwork__inside">
      <h2 className="afterwork__inside-title">
        {textServices.findByText('afterwork/inside/title', 'Inside Afterwork')}
      </h2>
      <p className="afterwork__inside-description">
        {textServices.findByText(
          'afterwork/inside/description',
          'Descubre los momentos que hicieron de cada edición una experiencia inolvidable.'
        )}
      </p>

      <div className="afterwork__inside-carousel" ref={carouselRef} onScroll={handleScroll}>
        {INSIDE_EDITIONS.map((edition) => (
          <button
            key={edition.key}
            type="button"
            className="afterwork__inside-card"
            onClick={() => {
              dlTrackEvent('user_interaction', { action: 'click', element: `ver ${edition.city.toLowerCase()}`, section: 'afterwork' })
              setActiveEdition(edition)
            }}
          >
            {/* Gradient placeholder stays behind; the still hides itself on 404 */}
            <div className="afterwork__inside-card-media afterwork__inside-card-media--placeholder" aria-hidden="true" />
            <picture>
              <source
                media="(min-width: 768px)"
                srcSet={encodeURI(`${AFTERWORK_ASSETS}/${edition.imageDesktop}`)}
              />
              <img
                className="afterwork__inside-card-media"
                src={encodeURI(`${AFTERWORK_ASSETS}/${edition.image}`)}
                alt=""
                aria-hidden="true"
                onError={(event) => {
                  event.currentTarget.style.display = 'none'
                }}
                onLoad={(event) => {
                  // The real stills bake the city name in: hide the DOM label
                  event.currentTarget.closest('.afterwork__inside-card')?.classList.add('afterwork__inside-card--with-media')
                }}
              />
            </picture>
            <span className="afterwork__inside-card-see">
              {textServices.findByText('afterwork/inside/see', 'VER +')}
            </span>
            <span className="afterwork__inside-card-city">{edition.city}</span>
          </button>
        ))}
      </div>

      <div className="afterwork__inside-dots">
        {INSIDE_EDITIONS.map((edition, index) => (
          <button
            key={edition.key}
            type="button"
            aria-label={`Ir a ${edition.city}`}
            className={`afterwork__inside-dot ${index === activeIndex ? 'afterwork__inside-dot--active' : ''}`.trim()}
            onClick={() => {
              dlTrackEvent('user_interaction', { action: 'click', element: `ir a ${edition.city.toLowerCase()}`, section: 'afterwork' })
              scrollToIndex(index)
            }}
          />
        ))}
      </div>

      {activeEdition && (
        <VideoLightbox
          title={textServices.findByText('afterwork/inside/lightboxTitle', ' ') as string}
          src={activeEdition.videoUrl}
          onClose={() => {
            dlTrackEvent('user_interaction', { action: 'click', element: 'cerrar video', section: 'afterwork' })
            setActiveEdition(null)
          }}
        />
      )}
    </section>
  )
}

export default InsideSection
